/**
 * Programmverwaltung - Liste
 * 
 * Funktionen:
 * - Liste mit Suche (Nummer, Name)
 * - Navigation zu Detailseite (Create/Edit)
 * 
 * UX-Leitlinie: Keine Modals für Create/Edit, nur Liste → Detailseite
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ListChecks, Plus, RefreshCw, Search, ChevronRight } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { StatusSelect, type StatusFilter } from '../../lib/listing';

type ProgramTypeColor = 'GREEN' | 'ORANGE' | 'PURPLE' | 'BLUE' | 'GRAY';

interface ProgramTypeEntity {
  id: string;
  code: string;
  name: string;
  color: ProgramTypeColor;
  isActive: boolean;
}

const sortAlpha = <T extends { name: string }>(items: T[]): T[] => {
  return [...items].sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }));
};

interface Program {
  id: string;
  code: string;
  number: string;
  name: string;
  type: string;
  programTypeId: string;
  programType?: ProgramTypeEntity;
  dosNumber: string | null;
  maxTemp: string | null;
  maxLoadPercent: number | null;
  remarks: string | null;
  isActive: boolean;
}

const COLOR_CLASSES: Record<ProgramTypeColor, string> = {
  GREEN: 'bg-green-100 text-green-800',
  ORANGE: 'bg-orange-100 text-orange-800',
  PURPLE: 'bg-purple-100 text-purple-800',
  BLUE: 'bg-blue-100 text-blue-800',
  GRAY: 'bg-gray-100 text-gray-800',
};

export default function ProgramsPage() {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [programTypes, setProgramTypes] = useState<ProgramTypeEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');

  // Load programs and program types
  const loadPrograms = async () => {
    setLoading(true);
    try {
      const [programsRes, typesRes] = await Promise.all([
        fetch('/api/admin/programs', { credentials: 'include' }),
        fetch('/api/admin/program-types', { credentials: 'include' }),
      ]);
      if (programsRes.ok) {
        const data = await programsRes.json();
        setPrograms(data);
      }
      if (typesRes.ok) {
        const data = await typesRes.json();
        setProgramTypes(data.filter((t: ProgramTypeEntity) => t.isActive));
      }
    } catch (err) {
      console.error('Fehler beim Laden der Programme:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadPrograms();
  }, []);

  // Filter (Nummer, Name, Status) + numerisch sortieren
  const filtered = useMemo(() => {
    const byStatus = status === 'all' 
      ? programs 
      : programs.filter(p => status === 'active' ? p.isActive !== false : p.isActive === false);
    const searched = !search ? byStatus : byStatus.filter(p => {
      const s = search.toLowerCase();
      return p.number.toLowerCase().includes(s) ||
        p.name.toLowerCase().includes(s) ||
        p.dosNumber?.toLowerCase().includes(s);
    });
    // Numerisch nach Programmnummer sortieren
    return searched.sort((a, b) => parseInt(a.number, 10) - parseInt(b.number, 10));
  }, [programs, search, status]);

  // Get program type by ID
  const getProgramType = (typeId: string) => programTypes.find(t => t.id === typeId);

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ListChecks className="w-6 h-6" />
              Programme
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {filtered.length} von {programs.filter(p => p.isActive !== false).length} Programmen
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadPrograms}
              disabled={loading}
              className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              title="Aktualisieren"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button 
              onClick={() => navigate('/admin/programs/new')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Neues Programm
            </button>
          </div>
        </div>

        {/* Search + Filter */}
        <div className="flex gap-4 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Suchen (Nummer, Name)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <StatusSelect value={status} onChange={setStatus} />
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Laden...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {search 
                ? 'Keine Treffer' 
                : status === 'inactive' 
                  ? 'Keine inaktiven Programme' 
                  : 'Keine Programme vorhanden'}
              {!search && status === 'active' && programs.length === 0 && (
                <button 
                  onClick={() => navigate('/admin/programs/new')}
                  className="block mx-auto mt-4 text-blue-600 hover:underline"
                >
                  Erstes Programm anlegen
                </button>
              )}
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Pr.Nr.</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase hidden sm:table-cell">Dos.Nr.</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase hidden md:table-cell">Max.Temp</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase hidden md:table-cell">Max.Bel.%</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase hidden sm:table-cell">Typ</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map(p => (
                  <tr 
                    key={p.id} 
                    className={`hover:bg-slate-50 cursor-pointer ${p.isActive === false ? 'opacity-50' : ''}`}
                    onClick={() => navigate(`/admin/programs/${p.id}`)}
                  >
                    <td className="px-4 py-3 font-mono font-bold">{p.number}</td>
                    <td className="px-4 py-3 font-mono text-slate-500 hidden sm:table-cell">{p.dosNumber || '-'}</td>
                    <td className="px-4 py-3 font-medium">{p.name}</td>
                    <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{p.maxTemp || '-'}</td>
                    <td className="px-4 py-3 text-right text-slate-600 hidden md:table-cell">{p.maxLoadPercent ? `${p.maxLoadPercent}%` : '-'}</td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      {(() => {
                        const pt = getProgramType(p.programTypeId);
                        return pt ? (
                          <span className={`px-2 py-1 text-xs rounded ${COLOR_CLASSES[pt.color] || 'bg-slate-100 text-slate-800'}`}>
                            {pt.name}
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded bg-slate-100 text-slate-800">-</span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
